import { createRouter, createWebHistory } from 'vue-router';
import OpenIdConnectCallback from './pages/OpenIdConnectCallback.vue';
import ErrorPage from './pages/ErrorPage.vue';
import { userManager } from '@/components/oidc';
import { User } from 'oidc-client-ts';
import PersonalInfoPage from './pages/PersonalInfoPage.vue';
import SecurityPage from './pages/SecurityPage/index.vue';
import SetPasswordPage from './pages/SetPasswordPage.vue';
import ResetPasswordPage from './pages/ResetPasswordPage.vue';
import InfoPage from './pages/InfoPage.vue';
import OTPPage from './pages/OTPPage.vue';
import { initLocale } from '@/lang';

/**
 * This pages are backed by FR, so the name must match the stage of the step.
 * Note: the OATH device registration was split in 2 pages because there are 2 auth-nodes
 */
export const FRPages = ['OATH-Registration1', 'OATH-Registration2'];
//type FRPage = typeof FRPages[number];

const routes = [
   { path: '/', redirect: '/personal-info' },
   { path: '/personal-info', component: PersonalInfoPage, meta: { requiresAuth: true, } },
   { path: '/sign-in-and-security', component: SecurityPage, meta: { requiresAuth: true, }, },
   { path: '/openid-connect-callback', component: OpenIdConnectCallback },
   { path: '/reset-password', component: ResetPasswordPage },
   { path: '/set-password', name: 'setPassword', component: SetPasswordPage },
   { path: '/otp', name: 'otp', component: OTPPage },
   { path: '/info/:key(\\w+)', name: 'info', component: InfoPage },
   { path: '/error/:status(\\d+)?', component: ErrorPage },
   { path: '/access-denied', name: '403', component: ErrorPage, props: { status: 403 } },
   { path: '/:pathMatch(.*)*', component: ErrorPage, props: { status: 404 } } // 404
];

const router = createRouter({
   history: createWebHistory(import.meta.env.BASE_URL),
   routes
});

const hasAccess = (user: User) => {
   const scopes = user.scopes;
   console.debug(`[router] scopes: ${scopes} profile:`, user.profile);
   if (user.profile && scopes && scopes.includes('openid')) {
      console.info(`[router] profile:${user.profile}`);
      return true;
   }
   console.info(`[router] role: none`);
   return false;
};

/**
 * router interceptor for checking the roles & starting the openid-connect protocol
 * @see https://github.com/joaojosefilho/vuejsOidcClient
 */
router.beforeEach(async (to, _from) => {
   //console.debug(`[router] to:${to.path}`)

   // set language
   const locale = to.query.locale as string ?? undefined;

   // handle authorization
   const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
   if (requiresAuth) {
      //console.debug(`[router] protected route ${to.path} ${to.params}`)

      await userManager.getUser()
         .then(
            async user => {
               console.debug(`[router] we have a token: ${user}, session-checked: ${window.sessionChecked}`);

               // check if user logged or expired or first-app-load
               if (!user || user.expired || typeof window.sessionChecked === 'undefined') {
                  console.log(`[router] session is expired. starting auth (state:${to.fullPath}, ui_locales:${locale})...`);
                  await userManager.startLogin(to.fullPath, locale);
                  return false; // cancel current route
               }

               if (hasAccess(user)) {
                  return true;
               } else {
                  console.error(`no access to ${to.path}`);
                  return { name: '403' };
               }
            },
            err => {
               console.log(`[router] login failed: ${err}`);
               return { name: '403' };
            }
         );
   } else {
      console.debug(`[router] public route ${to.path}`);
      initLocale(locale);
      return true;
   }
});

export default router;
